import { createAsyncThunk } from '@reduxjs/toolkit';
import { t } from 'i18next';
import { isEmpty, isString } from 'lodash';

import { Gender, Language } from 'config/enums';
import {
  sendAnalyticsInfo,
  sendAnalyticsWarning,
} from 'features/analytics/helpers';
import type { RootState } from 'features/app/store/store';
import { AudioPlayer } from 'features/audio-player/services';
import { LogoutType } from 'features/call/call-base/enums';
import { CallDaoService } from 'features/call/call-base/services';
import {
  handleResetCall,
  setCallTakenTime,
} from 'features/call/call-base/store';
import {
  callIdSelector,
  callLanguageCodeSelector,
} from 'features/call/call-base/store/selectors';
import { isDeafConnectedSelector } from 'features/call/call-deaf/store';
import { CallNotificationType } from 'features/call/call-events/enums';
import { handleServiceResets } from 'features/call/call-events/helpers';
import {
  acceptTeamingOffer,
  teamingCanceled,
} from 'features/call/call-ui-state/store';
import { setPreviousCallId } from 'features/call/vrs-call-comments/store';
import { resetVrsCall } from 'features/call/vrs-call/store';
import { wasCallPlacedSelector } from 'features/call/vrs-call/store/vrsCallSelectors';
import { callerSessionAnalyticsInfo } from 'features/caller-session/helpers';
import { incomingCallAccepted } from 'features/caller-session/store';
import { handleError, showNotification } from 'features/notification/store';
import {
  hearingConnectRecordedSelector,
  isSignMailConnectRecordedSelector,
  twoLineVCORecordedSelector,
} from 'features/reporting/store';
import type { Agent } from 'features/rns/interfaces';
import { sendRnsNotification } from 'features/rns/store';
import { SessionDaoService } from 'features/session/services';
import {
  createSession,
  resetSessionId,
  rnsConnectionIdSelector,
} from 'features/session/store';
import { sipSettings } from 'features/sip/config';
import { RtcSessionTerminateCauses } from 'features/sip/enums';
import { SipSessionEventBus } from 'features/sip/services';
import type {
  AdhocTeamRequestDaoOptions,
  AdhocTeamRequestFormOptions,
} from 'features/teaming/teaming-adhoc/interfaces';
import {
  TeamingReason,
  TeamingStatus,
  TeamingType,
} from 'features/teaming/teaming-base/enums';
import type {
  CompleteTeamingPayload,
  RestoreTeamingPayload,
  TeamRequestDaoOptions,
  TeamRequestFormOptions,
  TeamingBeginPayload,
  TeamingRemovePayload,
} from 'features/teaming/teaming-base/interfaces';
import { TeamingDaoService } from 'features/teaming/teaming-base/services';
import {
  isAcceptingUserSelector,
  isRemoteTeamingSelector,
  isRequestingUserSelector,
  resetTeaming,
  resetTeamingFreeSwitchCallStillInProgress,
  setTeamingAcceptingUser,
  setTeamingAnotherUserSessionId,
  setTeamingOptions,
  setTeamingRequestingUser,
  setTeamingStatus,
  teamingAcceptingUserIdSelector,
  teamingAcceptingUserSelector,
  teamingReasonSelector,
  teamingRoomUriSelector,
  teamingStatusSelector,
} from 'features/teaming/teaming-base/store';
import type {
  AnalyticsConfig,
  CallAttemptConfig,
} from 'features/teaming/teaming-base/types';
import {
  handleCallFailure,
  handleTeamingCallSuccess,
  makeCallAttempt,
} from 'features/teaming/teaming-base/utils/';
import { UserDaoService } from 'features/user/services';
import { userIdSelector, userSelector } from 'features/user/store';
import { RetryManager, toIsoWithTimezone } from 'features/utils/helpers';
import { VoiceMeetingEventBus } from 'features/voice-meeting/services';
import { voiceSessionIdSelector } from 'features/voice-session/store';

export const takeRemoteTeamingRequest = createAsyncThunk(
  'teaming/takeRemote',
  async (_payload, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      const userId = userIdSelector(state);
      const sessionId = rnsConnectionIdSelector(state);
      const call = await CallDaoService.takeCall({
        callId,
        userId,
        sessionId,
      });
      dispatch(
        setCallTakenTime(toIsoWithTimezone(call.TakenOrAbandonDateTime))
      );
      dispatch(acceptTeamingOffer());
      dispatch(
        incomingCallAccepted(callId.toString(), call.CallerConnectionString)
      );
      dispatch(callerSessionAnalyticsInfo('Accepted Teaming Call'));
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'takeRemoteTeamingRequest',
        })
      );
    } finally {
      AudioPlayer.stop();
    }
  }
);

export const takeInPersonTeamingRequest = createAsyncThunk(
  'teaming/takeInPerson',
  async (_payload, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      const userId = userIdSelector(state);
      const sessionId = rnsConnectionIdSelector(state);
      await CallDaoService.takeCall({
        callId,
        userId,
        sessionId,
      });
      dispatch(resetSessionId());
      dispatch(handleResetCall()).unwrap();
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'takeInPersonTeamingRequest',
        })
      );
    } finally {
      AudioPlayer.stop();
    }
  }
);

export const declineTeamingRequest = createAsyncThunk(
  'teaming/decline',
  async (_payload, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      await CallDaoService.denyCall({
        callId,
      });
      dispatch(handleResetCall()).unwrap();
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'declineTeamingRequest',
        })
      );
    } finally {
      AudioPlayer.stop();
    }
  }
);

export const restoreTeamingRequest = createAsyncThunk(
  'teaming/restoreRequest',
  async (payload: RestoreTeamingPayload, { dispatch, getState }) => {
    const { teaming } = getState() as RootState;
    // we are waiting to check if the deaf is actually connected because this is a race condition and sometimes this gets called first.
    await new Promise((resolve) => setTimeout(resolve, 1800));
    const isDeafConnected = isDeafConnectedSelector(getState() as RootState);
    if (!isDeafConnected) {
      dispatch(resetTeaming());
      sendAnalyticsInfo({
        Method: 'restoreTeamingRequest',
        message:
          'Deaf user disconnected so we are not allowing another request to be restored',
      });
      return;
    }

    try {
      await TeamingDaoService.requestTeaming({
        callId: payload.callId,
        options: {
          reason: teaming.reason,
          gender: teaming.gender,
          language: teaming.language,
          teamingType: teaming.teamingType,
          isRemote: teaming.isRemote,
        },
      });

      dispatch(setTeamingStatus(TeamingStatus.DECLINED));
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'restoreTeamingRequest',
        })
      );
    }
  }
);

export const cancelTeaming = createAsyncThunk(
  'teaming/cancel',
  async (_payload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    try {
      await TeamingDaoService.cancelTeaming(callId);
      dispatch(resetTeamingFreeSwitchCallStillInProgress());
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'cancelTeaming',
        })
      );
    }
  }
);

export const cancelTeamingCall = createAsyncThunk(
  'teaming/cancelCall',
  async (_payload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const userId = userIdSelector(state);
    try {
      await TeamingDaoService.completeTeaming({
        callId,
        userId,
      });
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'cancelTeamingCall',
        })
      );
    } finally {
      dispatch(handleResetCall()).unwrap();
    }
  }
);

export const completeTeamingCall = createAsyncThunk(
  'teaming/complete',
  async ({ userId }: CompleteTeamingPayload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);

    try {
      await TeamingDaoService.completeTeaming({
        callId,
        userId,
      });
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'completeTeamingCall',
        })
      );
    }
  }
);

const DEFAULT_RETRY_ATTEMPTS = 2; // Maximum 3 attempts total (initial + 2 retries)
const BASE_DELAY = 1000; // 1 second base delay

export const callTeamingRoom = createAsyncThunk(
  'teaming/begin',
  async (
    { uaRef, sipHeaders, isAcceptingAgent }: TeamingBeginPayload,
    { dispatch, getState }
  ) => {
    const { teaming } = getState() as RootState;
    const sipConfig = sipSettings.get();

    const callConfig: CallAttemptConfig = {
      uaRef,
      roomUri: teaming.roomUri,
      sipHeaders,
      sipConfig,
    };

    const analyticsConfig: AnalyticsConfig = {
      isAcceptingAgent: !!isAcceptingAgent,
      roomUri: teaming.roomUri,
    };

    const retryManager = RetryManager.withJitter(
      BASE_DELAY,
      DEFAULT_RETRY_ATTEMPTS + 1
    );

    // Main operation: attempt to make the teaming call
    const attemptTeamingCall = async (context: any) => {
      const connectionSuccessful = await makeCallAttempt(
        callConfig,
        dispatch,
        context.isRetry
      );

      if (!connectionSuccessful) {
        throw new Error('Connection attempt failed');
      }

      return connectionSuccessful;
    };

    // Handle final success
    const handleSuccess = (_result: any, context: any) => {
      handleTeamingCallSuccess(
        dispatch,
        analyticsConfig,
        context.attemptNumber
      );
    };

    // Handle final failure
    const handleFailure = (context: any) => {
      handleCallFailure(
        dispatch,
        analyticsConfig,
        !!isAcceptingAgent,
        context.attemptNumber,
        leaveRemoteTeaming
      );
    };

    // Execute with retry logic
    await retryManager.execute(attemptTeamingCall, {
      onSuccess: handleSuccess,
      onFailure: handleFailure,
      analytics: {
        dispatch,
        method: 'callTeamingRoom',
        message: 'Attempting teaming call connection',
        roomUri: teaming.roomUri,
        isAcceptingAgent: !!isAcceptingAgent,
      },
    });
  }
);

export const handleTeamingNotification = createAsyncThunk(
  'teaming/handleNotification',
  async (_payload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const acceptingUser = teamingAcceptingUserSelector(state);

    dispatch(
      showNotification({
        severity: 'info',
        title: t('teamNotification.remoteUserLogout', {
          userNumber: acceptingUser.number,
          userName: acceptingUser.name,
        }),
      })
    );
  }
);

export const handleTeamingFinish = createAsyncThunk(
  'teaming/handleFinish',
  async (_payload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const isRequestingUser = isRequestingUserSelector(state);
    const isAcceptingUser = isAcceptingUserSelector(state);
    if (isRequestingUser) {
      dispatch(resetTeamingFreeSwitchCallStillInProgress());
    }

    if (isAcceptingUser) {
      VoiceMeetingEventBus.finishVoiceSession$.next();
      SipSessionEventBus.terminate$.next({
        cause: RtcSessionTerminateCauses.LEAVE_TEAMING,
      });
      dispatch(handleResetCall()).unwrap();
    }
  }
);

export const leaveRemoteTeaming = createAsyncThunk(
  'teaming/leave',
  async (_payload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const userId = userIdSelector(state);
    const teamingStatus = teamingStatusSelector(state);
    if (teamingStatus === TeamingStatus.NONE) {
      dispatch(
        sendAnalyticsWarning({
          message: 'Teaming status is none, VI cannot leave teaming call',
          Method: 'leaveRemoteTeaming',
        })
      );
      return;
    }

    try {
      await TeamingDaoService.completeTeaming({
        callId,
        userId,
      });
      SipSessionEventBus.terminate$.next({
        cause: RtcSessionTerminateCauses.LEAVE_TEAMING,
      });
      VoiceMeetingEventBus.resetVoiceSessionTeamingLeave$.next();

      dispatch(setPreviousCallId(callId));
      dispatch(handleResetCall()).unwrap();
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'leaveRemoteTeaming',
        })
      );
    }
  }
);

export const leaveInPersonTeaming = createAsyncThunk(
  'teaming/leave',
  async (_payload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const userId = userIdSelector(state);

    try {
      await TeamingDaoService.completeTeaming({
        callId,
        userId,
      });

      dispatch(resetTeaming());
      dispatch(resetVrsCall());
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'leaveInPersonTeaming',
        })
      );
    }
  }
);

export const removeTeam = createAsyncThunk(
  'teaming/remove',
  async (
    payload: TeamingRemovePayload,
    { dispatch, getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);

    try {
      await TeamingDaoService.completeTeaming({
        callId,
        userId: payload.userId,
        suppressTeamEndNotify: payload?.suppressTeamEndNotify,
      });
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'removeTeam',
        })
      );
      return rejectWithValue(error);
    }
  }
);

export const handleTeamingCancel = createAsyncThunk(
  'teaming/handleCancel',
  async (_payload, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const rnsConnectionId = rnsConnectionIdSelector(state);
      await SessionDaoService.removeSession({
        logoutType: LogoutType.Normal,
        rnsConnectionId,
      });
      dispatch(resetTeamingFreeSwitchCallStillInProgress());
      dispatch(teamingCanceled());
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'handleTeamingCancel',
        })
      );
    }
  }
);

export const returnToQueue = createAsyncThunk(
  'teaming/returnToQueue',
  async (_payload, { dispatch, getState }) => {
    const state = getState() as RootState;
    const sessionId = rnsConnectionIdSelector(state);
    try {
      dispatch(createSession(sessionId)).unwrap();
      handleServiceResets();
      dispatch(handleResetCall()).unwrap();
    } catch (error) {
      dispatch(handleError({ error }));
    }
  }
);

export const completeTeaming = createAsyncThunk(
  'teaming/complete',
  async (_payload, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const teamingStatus = teamingStatusSelector(state);
    const callId = callIdSelector(state);
    const isAcceptingUser = isAcceptingUserSelector(state);
    const acceptingUser = teamingAcceptingUserSelector(state);

    if (isAcceptingUser) {
      return;
    }

    try {
      if (
        teamingStatus === TeamingStatus.ACCEPTED ||
        teamingStatus === TeamingStatus.STARTED
      ) {
        await TeamingDaoService.completeTeaming({
          callId,
          userId: acceptingUser.id,
        });
      }

      if (
        teamingStatus === TeamingStatus.REQUESTED ||
        teamingStatus === TeamingStatus.DECLINED
      ) {
        await TeamingDaoService.cancelTeaming(callId);
      }
    } catch (error) {
      dispatch(handleError({ error }));
      return rejectWithValue(error);
    }
  }
);

export const requestRemoteTeaming = createAsyncThunk(
  'teaming/requestRemote',
  async (options: TeamRequestDaoOptions, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const voiceSessionId = voiceSessionIdSelector(state);

    // Use Gender enum for mapping
    let genderRequested = 'Unspecified';
    if (options.gender === Gender.M) {
      genderRequested = 'Male';
    } else if (options.gender === Gender.F) {
      genderRequested = 'Female';
    }

    dispatch(
      sendAnalyticsInfo({
        message: 'Teaming Remote Requested',
        Method: 'requestRemoteTeaming',
        TeamingReason: options.reason,
        GenderRequested: genderRequested,
        isRemote: options.isRemote,
      })
    );

    try {
      const roomUri = await TeamingDaoService.requestTeaming({
        callId,
        voiceSessionId,
        options: options,
      });
      if (!isString(roomUri) || isEmpty(roomUri?.trim())) {
        dispatch(
          sendAnalyticsInfo({
            message: 'TeamingDaoService.requestTeaming returned an empty URI',
            Method: 'requestRemoteTeaming',
            TeamingReason: options.reason,
            isRemote: options.isRemote,
            response: roomUri,
          })
        );
      }
      dispatch(
        setTeamingOptions({
          ...options,
          roomUri,
        })
      );
    } catch (error) {
      dispatch(handleError({ error, methodName: 'requestRemoteTeaming' }));
    }
  }
);
export const requestAdhocTeaming = createAsyncThunk(
  'teaming/requestAdhoc',
  async (options: AdhocTeamRequestFormOptions, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const callLanguageCode = callLanguageCodeSelector(state);
    const user = userSelector(state);

    try {
      const requestOptions: AdhocTeamRequestDaoOptions = {
        adhocUsername: options.adhocUsername,
      };

      dispatch(
        setTeamingRequestingUser({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
        })
      );

      const adhocUser = await TeamingDaoService.requestAdhocTeaming({
        callId,
        options: requestOptions,
      });

      dispatch(
        setTeamingOptions({
          gender: adhocUser.genderCode,
          teamingType: TeamingType.AdhocReq,
          reason: TeamingReason.Other,
          language: Language[callLanguageCode as Language],
          roomUri: '',
          isRemote: false,
        })
      );

      dispatch(
        setTeamingAcceptingUser({
          id: adhocUser.id,
          number: adhocUser.agentNumber,
          name: `${adhocUser.firstName} ${adhocUser.lastName}`,
        })
      );

      await TeamingDaoService.beginTeaming(callId);
      dispatch(setTeamingStatus(TeamingStatus.STARTED));
    } catch (error) {
      dispatch(resetTeamingFreeSwitchCallStillInProgress());
      dispatch(
        handleError({
          error,
          methodName: 'requestAdhocTeaming',
        })
      );
    }
  }
);

export const requestInPersonTeaming = createAsyncThunk(
  'teaming/requestInPerson',
  async (options: TeamRequestFormOptions, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const voiceSessionId = voiceSessionIdSelector(state);

    const requestOptions: TeamRequestDaoOptions = {
      reason: TeamingReason[options.reason],
      gender: Number(options.gender),
      language: options.language,
      teamingType: options.location,
      isRemote: false,
    };

    await TeamingDaoService.requestTeaming({
      callId,
      voiceSessionId,
      options: requestOptions,
    });
    dispatch(
      setTeamingOptions({
        ...requestOptions,
      })
    );
    dispatch(setTeamingStatus(TeamingStatus.REQUESTED));
  }
);

export const setAnotherTeamingUserSessionId = createAsyncThunk(
  'teaming/setAnotherTeamingUserSessionId',
  async (user: Agent, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const isRemoteTeaming = isRemoteTeamingSelector(state);

    if (!isRemoteTeaming) {
      return;
    }

    try {
      const session = await UserDaoService.getUserSession(user.Id);
      dispatch(setTeamingAnotherUserSessionId(session.SessionId));
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const setTeamingAcceptingUserInfo = createAsyncThunk(
  'teaming/setTeamingAcceptingUserInfo',
  async (user: Agent, { dispatch, rejectWithValue }) => {
    dispatch(
      setTeamingAcceptingUser({
        id: user.Id,
        number: user.AgentNumber,
        name: `${user.FirstName} ${user.LastName}`,
      })
    );

    try {
      await dispatch(setAnotherTeamingUserSessionId(user)).unwrap();
    } catch (error) {
      dispatch(
        handleError({
          message: t('teaming.requestErrorMessages.setAcceptingUserSessionId'),
          error,
        })
      );
      return rejectWithValue(error);
    }
  }
);

export const setTeamingRequestingUserInfo = createAsyncThunk(
  'teaming/setTeamingRequestingUserInfo',
  async (user: Agent, { dispatch, rejectWithValue }) => {
    dispatch(
      setTeamingRequestingUser({
        id: user.Id,
        name: user.FullName,
      })
    );

    try {
      await dispatch(setAnotherTeamingUserSessionId(user)).unwrap();
    } catch (error) {
      dispatch(
        handleError({
          message: t('teaming.requestErrorMessages.setRequestingUserSessionId'),
          error,
        })
      );
      return rejectWithValue(error);
    }
  }
);

export const forceAcceptTeaming = createAsyncThunk(
  'teaming/forceAcceptTeaming',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      const reason = teamingReasonSelector(state);
      const roomUri = teamingRoomUriSelector(state);
      const teamingAcceptingUserId = teamingAcceptingUserIdSelector(state);

      await TeamingDaoService.forceAcceptTeaming({
        callId,
        userId: teamingAcceptingUserId,
        reason,
        roomUri,
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendInfoToViThunk = createAsyncThunk(
  'teaming/sendInfoToVi',
  async (eventType: CallNotificationType, { dispatch, getState }) => {
    const state = getState() as RootState;
    const isRequestingUser = isRequestingUserSelector(state);
    if (!isRequestingUser) {
      return;
    }

    const eventDataMap = {
      [CallNotificationType.PRIMARY_HEARING_CONNECT_RECORDED]: {
        primaryHearingConnectWasRecorded: true,
      },
      [CallNotificationType.PRIMARY_HEARING_DISCONNECT_RECORDED]: {
        primaryHearingDisconnectWasRecorded: true,
      },
      [CallNotificationType.SIGN_MAIL_CONNECT_RECORDED]: {
        signMailConnectWasRecorded: true,
      },
      [CallNotificationType.TWO_LINE_VCO_CONNECT_RECORDED]: {
        twoLineVcoConnectWasRecorded: true,
      },
      [CallNotificationType.TWO_LINE_VCO_DISCONNECT_RECORDED]: {
        twoLineVcoDisconnectWasRecorded: true,
      },
      [CallNotificationType.SIGN_MAIL_DISCONNECT_RECORDED]: {
        signMailDisconnectWasRecorded: true,
      },
    };

    // Handle common notification cases
    if (eventType in eventDataMap) {
      dispatch(
        sendRnsNotification({
          eventName: CallNotificationType.INFO_FOR_VI,
          data: eventDataMap[eventType as keyof typeof eventDataMap],
        })
      );
      return;
    }

    // Handle special case for INITIAL_INFO_TEAMING
    if (eventType === CallNotificationType.INITIAL_INFO_TEAMING) {
      const wasCallPlaced = wasCallPlacedSelector(state);
      const isHearingConnectRecorded = hearingConnectRecordedSelector(state);
      const isTwoLineVcoConnectRecorded = twoLineVCORecordedSelector(state);
      const isSignMailConnectRecorded =
        isSignMailConnectRecordedSelector(state);
      dispatch(
        sendRnsNotification({
          eventName: CallNotificationType.INFO_FOR_VI,
          data: {
            callWasPlaced: wasCallPlaced,
            primaryHearingConnectWasRecorded: isHearingConnectRecorded,
            twoLineVcoConnectWasRecorded: isTwoLineVcoConnectRecorded,
            signMailConnectWasRecorded: isSignMailConnectRecorded,
          },
        })
      );

      dispatch(
        sendAnalyticsInfo({
          message: `Info for was call placed VI sent to VI2 with the value of ${wasCallPlaced}`,
          Method: 'sendInfoToViThunk',
        })
      );
    }
  }
);

export const leaveRemoteTeamingConference = createAsyncThunk(
  'teaming/leaveRemoteConference',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const userId = userIdSelector(state);

    try {
      await TeamingDaoService.completeTeaming({
        callId,
        userId,
      });
    } catch (error) {
      dispatch(handleError({ error }));
    }
  }
);
